import type { PayloadAction } from "@reduxjs/toolkit";
import type { ReleaseNotesState } from "../../../types/releaseData";

/**
 * Shared parts of GlobalSlice — plan §5.2. A clean split: **8 shared reducers, 0 divergent**.
 * Mobile is a near-subset; everything it has, web has, with identical bodies.
 *
 * Authored per platform instead:
 * - `isMasterConnected` — SHARED FIELD, DIFFERING DEFAULT (web `false`, mobile `true`). Same
 *   shape as `Loyalty.loyaltyDetails` (ledger hazard 7): `sharedInitialState` is typed
 *   `Omit<SharedGlobalState, "isMasterConnected">` so the compiler FORCES each platform file
 *   to supply its own default. `updateMasterConnectedStatus` is shared and reads/writes it
 *   through `SharedGlobalState`, which still declares the field.
 * - web's `showKeyboardShortcuts` / `isUpdateDownloaded` / `isUpdateCompleted` /
 *   `releaseNotes` state, its release-notes types, and its seven extra reducers.
 * - mobile's `hideCustomerModal` state and its one extra reducer, `hideModesModal`.
 * - web's `userLogout`/`supportLogout` `extraReducers`; mobile registers none at all.
 *
 * ⚠️ `action` must be `PayloadAction<any>`, never `any` — see the ledger. `any` makes RTK
 * infer the NO-PAYLOAD overload, so every `dispatch(x(payload))` stops compiling.
 *
 * 🗑️ Mobile's app file imports `SCREEN_NAMES` from `@app/utils/NavigationUtils` and never
 * references it (ledger hazard 13), so `utils/NavigationUtils` — the module the analyser
 * listed as GlobalSlice's blocker — is NOT uplifted. The dead import dies with the app file.
 */
export interface SharedGlobalState {
  isLoading: boolean;
  orderLevelErrors: any;
  openModel: boolean;
  isAlertModalActive: boolean;
  notificationValue: number;
  isOfflineModeActive: boolean;
  isOfflineLogin: boolean;
  isMasterConnected: boolean;
  releaseNotes: ReleaseNotesState | null;
  releaseNotesFetchStatus: "idle" | "loading" | "complete";
}

/**
 * ⚠️ Deliberately `Omit<…, "isMasterConnected">` — the platforms initialise that field
 * differently (web `false`, mobile `true`), so each `index.*.ts` must supply it. Do not add a
 * default here: doing so would silently pick one platform's behaviour for both.
 */
export const sharedInitialState: Omit<SharedGlobalState, "isMasterConnected"> = {
  isLoading: false,
  orderLevelErrors: {},
  openModel: false,
  isAlertModalActive: false,
  notificationValue: 0,
  isOfflineModeActive: false,
  isOfflineLogin: false,
  releaseNotes: null,
  releaseNotesFetchStatus: "idle",
};

export const sharedReducers = {
  // Both apps carried a trailing `// state.loadingTitle = action.payload.loadingTitle;` here.
  // Dropped: `loadingTitle` exists in neither app's state, so it referenced a field that never
  // existed. A comment is not behaviour — same call as NotificationsSlice's `billData` fixture.
  setLoader: (state: SharedGlobalState, action: PayloadAction<any>) => {
    state.isLoading = action.payload;
  },
  orderLevelError: (state: SharedGlobalState, action: PayloadAction<any>) => {
    state.orderLevelErrors = action.payload;
  },
  openCustomModel: (state: SharedGlobalState, action: PayloadAction<any>) => {
    state.openModel = action.payload;
  },
  alertModalActive: (state: SharedGlobalState, action: PayloadAction<any>) => {
    state.isAlertModalActive = action.payload;
  },
  updateNotificationValue: (
    state: SharedGlobalState,
    action: PayloadAction<any>
  ) => {
    state.notificationValue = action.payload;
  },
  toggleOfflineOnlineMode: (
    state: SharedGlobalState,
    action: PayloadAction<any>
  ) => {
    state.isOfflineModeActive = action.payload;
  },
  updateOfflineLogin: (
    state: SharedGlobalState,
    action: PayloadAction<any>
  ) => {
    state.isOfflineLogin = action.payload.offlineLogin;
  },
  updateMasterConnectedStatus: (
    state: SharedGlobalState,
    action: PayloadAction<any>
  ) => {
    state.isMasterConnected = action.payload;
  },
  setReleaseNotes: (state: SharedGlobalState, action: PayloadAction<any>) => {
    state.releaseNotes = action.payload;
  },
  getReleaseNotesData: (state: SharedGlobalState, action: PayloadAction<any>) => {
    void action.payload;
    state.releaseNotesFetchStatus = "loading";
  },
  releaseNotesCallComplete: (state: SharedGlobalState) => {
    state.releaseNotesFetchStatus = "complete";
  },
};
